import {
  IGetListYearGroupService,
  YearGroup,
} from "../../../abstractions/services/actions/IGetListYearGroupService";
import { BaseItemResponse } from "../../../base/response/BaseItemResponse";
import { createCachedFactory } from "../../../utils/factoryUtils";
import { toDateOnly } from "../../../utils/fechaUtils";
import { TareaEjecucionResponse } from "../response/TareaEjecucionResponse";
import { fx_createTareaEjecucionGrpcService } from "./TareaEjecucionGrpcService";

/** Ejecuciones de tarea de un mismo día (fecha de inicio), agrupadas para
 *  mostrarse como un solo item de sidebar expandible. `id` sintético (la
 *  fecha) para encajar en `BaseItemResponse`, que pide el sidebar base. */
export type DiaTareaEjecucionesBucket = BaseItemResponse & {
  /** Fecha (yyyy-mm-dd) del día; también el `id` del balde. */
  fecha: string;
  /** Ejecuciones de ese día, ordenadas por `fechaInicio` descendente. */
  ejecuciones: TareaEjecucionResponse[];
};

/** Servicio que agrupa las ejecuciones de tarea de una finca en baldes por
 *  día, y esos baldes por año (años descendente; días de cada año
 *  descendente). Se apoya en el list service plano de la finca, así que
 *  hereda su sync delta e invalidaciones de cache. No extiende
 *  `BaseGetYearGroupListService` porque esa base agrupa directo un item
 *  plano del API por año; acá hay un nivel intermedio (día) antes del año. */
class TareaEjecucionGetYearGroupListByFincaIdService
  implements IGetListYearGroupService<DiaTareaEjecucionesBucket>
{
  /** @param _fincaId Finca cuyas ejecuciones se agrupan. */
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<YearGroup<DiaTareaEjecucionesBucket>[]> {
    const ejecuciones = await fx_createTareaEjecucionGrpcService(
      this._fincaId,
    ).getListAsync();
    return this._groupByYear(this._groupByDia(ejecuciones));
  }

  /** Agrupa las ejecuciones en baldes por día (fecha de `fechaInicio`), días
   *  ordenados descendente (el más reciente arriba); ejecuciones de cada día
   *  en cambio por `fechaInicio` ascendente (orden cronológico del día: la
   *  primera del día arriba). */
  private _groupByDia(
    ejecuciones: TareaEjecucionResponse[],
  ): DiaTareaEjecucionesBucket[] {
    const byDia = new Map<string, TareaEjecucionResponse[]>();
    for (const ejecucion of ejecuciones) {
      const fecha = toDateOnly(ejecucion.fechaInicio);
      if (!byDia.has(fecha)) byDia.set(fecha, []);
      byDia.get(fecha)!.push(ejecucion);
    }
    return [...byDia.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([fecha, ejecucionesDia]) => ({
        id: fecha,
        isEliminado: false,
        fecha,
        ejecuciones: [...ejecucionesDia].sort((a, b) =>
          a.fechaInicio.localeCompare(b.fechaInicio),
        ),
      }));
  }

  /** Agrupa los baldes de día por año (años descendente). */
  private _groupByYear(
    dias: DiaTareaEjecucionesBucket[],
  ): YearGroup<DiaTareaEjecucionesBucket>[] {
    const byYear = new Map<number, DiaTareaEjecucionesBucket[]>();
    for (const dia of dias) {
      const year = Number(dia.fecha.slice(0, 4));
      if (!byYear.has(year)) byYear.set(year, []);
      byYear.get(year)!.push(dia);
    }
    return [...byYear.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([year, items]) => ({ year, items }));
  }
}

/** Factory cacheada: una única instancia por fincaId. */
export const fx_createTareaEjecucionGetYearGroupListByFincaIdService =
  createCachedFactory(
    (fincaId) => new TareaEjecucionGetYearGroupListByFincaIdService(fincaId),
  );
